import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import ChangeRequestModal from "./ChangeRequestModal";

const MEAL_LABELS = { kahvalti: "Kahvaltı", ogle: "Öğle", aksam: "Akşam" };
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pencil, Trash2, CheckCircle2, FileEdit, Printer, Receipt, FileText, Sparkles } from "lucide-react";
import { exportBrandedPdf } from "@/lib/pdfExport";

export default function AcenteReservationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAcenteAuth();
  const [data, setData] = useState(null);
  const [pricing, setPricing] = useState({ room_price: 0, meal_price: 0, total_price: 0, ops_note: "" });
  const [showChangeModal, setShowChangeModal] = useState(false);

  const load = () => {
    panelApi
      .get(`/reservations/${id}`)
      .then((r) => {
        setData(r.data);
        setPricing({
          room_price: r.data.room_price || 0,
          meal_price: r.data.meal_price || 0,
          total_price: r.data.total_price || 0,
          ops_note: r.data.ops_note || "",
        });
      })
      .catch(() => toast.error("Rezervasyon yüklenemedi"));
  };
  useEffect(() => { load(); }, [id]);

  const savePricing = async () => {
    try {
      await panelApi.patch(`/reservations/${id}/pricing`, pricing);
      toast.success("Fiyatlandırma kaydedildi");
      load();
    } catch (e) {
      toast.error(e?.friendly || e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const del = async () => {
    if (!window.confirm("Rezervasyonu silmek istediğinizden emin misiniz?")) return;
    try {
      await panelApi.delete(`/reservations/${id}`);
      toast.success("Silindi");
      nav("/acente/rezervasyonlar");
    } catch (e) {
      toast.error(e?.friendly || e?.response?.data?.detail || "Silinemedi");
    }
  };

  if (!data) return <div className="text-slate-500">Yükleniyor...</div>;

  // Acente: never direct-edit → always via change-request. Staff: direct edit.
  // Özel acente: direct edit their own (B2C rapid flow, low-volume, no revision workflow).
  const today = new Date().toISOString().slice(0, 10);
  const isPastCheckout = (data.checkout || "") < today;
  const canDirectEdit = ["admin", "operasyon", "ozel_acente"].includes(user?.role);
  const canRequestChange = user?.role === "acente" && !isPastCheckout;
  // Fiyat onayı SADECE operasyon rolüne aittir (Iter 39). Admin görebilir
  // ama onaylayamaz — operasyon disiplinini korumak için.
  const canPrice = user?.role === "operasyon";
  const canDelete = ["admin", "operasyon", "ozel_acente"].includes(user?.role);
  const canRequestInvoice = ["acente", "ozel_acente"].includes(user?.role);
  const canOpsApprove = user?.role === "operasyon";

  const approveNow = async () => {
    if (!data) return;
    try {
      await panelApi.post(`/reservations/${id}/ops-review`, { approve: true });
      toast.success("Fiyat onaylandı ve kilitlendi");
      load();
    } catch (e) {
      toast.error(e?.friendly || e?.response?.data?.detail || "Onaylanamadı");
    }
  };

  const requestInvoice = async () => {
    const label = data.group_name || data.customer_phone || data.reservation_number;
    if (!window.confirm(`"${label}" için fatura talep edilsin mi?`)) return;
    try {
      await panelApi.post("/invoice-requests", { request_type: "group", reservation_id: id, note: "" });
      toast.success("Faturanız 1 iş günü içerisinde tarafınıza iletilecektir.", { duration: 6000 });
    } catch (e) {
      toast.error(e?.friendly || e?.response?.data?.detail || "Talep gönderilemedi");
    }
  };

  return (
    <div className="space-y-6" data-testid="acente-reservation-detail">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <button onClick={() => nav(-1)} className="text-sm text-brand-navy/60 hover:text-brand-orange inline-flex items-center gap-1">
            <ArrowLeft size={14} /> Geri
          </button>
          <h1 className="mt-2 font-serif text-3xl text-brand-navy" data-testid="acente-detail-title">
            {data.group_name || data.customer_phone || "—"}
          </h1>
          <div className="text-xs font-mono text-slate-500">{data.reservation_number}</div>
          {data.customer_phone && (
            <div className="text-xs text-brand-navy/70 mt-1 inline-flex items-center gap-1" data-testid="acente-detail-phone-header">
              <span className="rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5">📞 {data.customer_phone}</span>
            </div>
          )}
          {data.hotel_name && (
            <div className="text-xs text-brand-navy/70 mt-1 inline-flex items-center gap-1" data-testid="acente-detail-hotel-header">
              <span className="rounded-full bg-brand-navy/5 border border-brand-navy/10 px-2 py-0.5">{data.hotel_name}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="acente-detail-print-btn" className="no-print">
            <Printer size={14} className="mr-1" /> Yazdır
          </Button>
          <Button
            onClick={() => {
              const totalHeads = (data?.adults || 0) + (data?.children || 0) + (data?.staff || 0);
              const totalRooms = ["r1","r2","r3","r4","r5"].reduce((s, k) => s + (data?.rooms?.[k] || 0), 0);
              const total = Number(data?.total_price || data?.agreed_room_price + data?.agreed_meal_price || 0);
              exportBrandedPdf({
                title: `Rezervasyon #${data?.reservation_number || ""}`.trim(),
                subtitle: data?.group_name || data?.customer_phone || "",
                columns: [
                  { key: "field", label: "Bilgi" },
                  { key: "val", label: "Değer" },
                ],
                rows: [
                  { field: "Grup Adı", val: data?.group_name || "—" },
                  { field: "Müşteri Telefon", val: data?.customer_phone || "—" },
                  { field: "Otel", val: data?.hotel_name || "—" },
                  { field: "Giriş Tarihi", val: formatTr(data?.checkin) },
                  { field: "Çıkış Tarihi", val: formatTr(data?.checkout) },
                  { field: "Gece", val: data?.nights || 0 },
                  { field: "Yetişkin / Çocuk / Görevli", val: `${data?.adults || 0} / ${data?.children || 0} / ${data?.staff || 0}` },
                  { field: "Toplam Kişi", val: totalHeads },
                  { field: "Toplam Oda", val: totalRooms },
                  { field: "Oda Dağılımı", val: ["r1","r2","r3","r4","r5"].map((k, i) => `${i+1} Kişilik: ${data?.rooms?.[k] || 0}`).join(" · ") },
                  { field: "Öğün", val: (data?.meals || []).map((m) => MEAL_LABELS[m] || m).join(", ") || "—" },
                  { field: "Toplam Ücret", val: `${Number(total).toLocaleString("tr-TR")} ﷼ (SAR)` },
                ],
                meta: {
                  "Rez. No": data?.reservation_number || "—",
                  "Acente": data?.agency_name || user?.agency_name || "—",
                  "Toplam Kişi": totalHeads,
                  "Toplam Oda": totalRooms,
                },
                footerNote: data?.notes || "",
              });
            }}
            disabled={!data}
            data-testid="acente-detail-pdf-btn"
            className="bg-gradient-to-r from-brand-navy to-brand-navy/90 hover:from-brand-orange-dark hover:to-brand-orange text-white no-print"
          >
            <FileText size={14} className="mr-1" /> PDF Oluştur
          </Button>
          {canOpsApprove && !((data?.total_price || 0) > 0) && (
            <Button
              onClick={approveNow}
              data-testid="acente-detail-ops-approve-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white no-print"
            >
              <CheckCircle2 size={14} className="mr-1" /> Fiyatı Onayla
            </Button>
          )}
          {canDirectEdit && (
            <Link to={`/acente/rezervasyon/${id}/duzenle`} className="inline-flex items-center gap-2 rounded-full border border-brand-navy/20 px-4 py-2 text-sm hover:bg-brand-navy/5 no-print" data-testid="acente-detail-edit-btn">
              <Pencil size={14} /> Düzenle
            </Link>
          )}
          {canRequestChange && (
            <Button
              onClick={() => setShowChangeModal(true)}
              className="bg-brand-orange text-white hover:bg-brand-orange-dark no-print"
              data-testid="acente-detail-change-btn"
            >
              <Pencil size={14} className="mr-1" /> Değişiklik Yap
            </Button>
          )}
          {canRequestInvoice && (
            <Button
              onClick={requestInvoice}
              className="bg-brand-gold text-brand-navy hover:brightness-95 no-print"
              data-testid="acente-detail-invoice-btn"
            >
              <Receipt size={14} className="mr-1" /> Fatura Talep Et
            </Button>
          )}
          {canDelete && (
            <Button variant="outline" onClick={del} data-testid="acente-detail-delete-btn" className="text-rose-600 hover:bg-rose-50 no-print">
              <Trash2 size={14} className="mr-1" /> Sil
            </Button>
          )}
        </div>
      </div>

      {/* Acente-only: read-only banner when it's their reservation */}
      {/* Rejection banner — shown to ALL roles when operasyon rejected the
          reservation with a reason. Bright red so the agent can't miss it;
          contains the operasyon note verbatim so the agent knows what to
          adjust before re-submitting. */}
      {data.ops_review_status === "rejected" && (
        <div
          className="rounded-2xl border-2 border-rose-400 bg-rose-50 px-5 py-4 flex items-start gap-3"
          data-testid="acente-detail-rejected-banner"
        >
          <div className="w-10 h-10 rounded-xl bg-rose-500 text-white flex items-center justify-center flex-shrink-0">
            <FileEdit size={18}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold">Operasyon Reddetti</div>
            <div className="font-serif text-lg text-rose-900 mt-0.5">Fiyat onaylanmadı — düzeltip tekrar iletin</div>
            {data.rejected_reason && (
              <div className="mt-2 rounded-lg bg-white border border-rose-200 px-3 py-2 text-sm text-rose-900" data-testid="acente-detail-rejected-reason">
                <span className="text-rose-600 uppercase tracking-widest text-[10px] font-semibold">Sebep:</span>
                <div className="mt-1 whitespace-pre-line">{data.rejected_reason}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {user?.role === "acente" && (
        <div className="rounded-2xl border border-brand-navy/10 bg-brand-navy/5 px-4 py-3 text-xs text-brand-navy/80 flex items-start gap-2" data-testid="acente-info-banner">
          <FileEdit size={14} className="mt-0.5 text-brand-orange"/>
          <div>
            {isPastCheckout ? (
              <>Bu rezervasyonun çıkış tarihi geçmiş, değişiklik yapılamaz. Fatura talebi için sağdaki butonu kullanabilirsiniz.</>
            ) : (
              <>Rezervasyon bilgilerini değiştirmek için <b>Değişiklik Yap</b> butonunu kullanın. Değişiklik yapıldığında rezervasyon "Revize" olarak işaretlenir.</>
            )}
          </div>
        </div>
      )}

      {/* Iter 42: "Değişiklik Yap" için görünür büyük CTA — acente rolü
          değişiklik yapmak istediğinde tepe menüsündeki küçük butonu
          arıyor. Bu banner formu daha kolay bulmasını sağlıyor. */}
      {canRequestChange && (
        <div
          className="rounded-2xl border-2 border-brand-orange/40 bg-gradient-to-br from-brand-orange/10 via-white to-white px-5 py-4 flex items-center justify-between gap-4 flex-wrap shadow-sm"
          data-testid="acente-detail-change-cta"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-brand-orange text-white flex items-center justify-center flex-shrink-0 shadow-md">
              <Pencil size={18}/>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-brand-orange font-semibold">Değişiklik mi Yapacaksınız?</div>
              <div className="font-serif text-lg text-brand-navy leading-tight">Rezervasyonda bir düzenleme yapmak istiyorsanız buradan başlayın</div>
              <p className="text-xs text-slate-600 mt-0.5">Talebiniz kaydedilir, admin/operasyon inceleyip onaylar.</p>
            </div>
          </div>
          <Button
            onClick={() => setShowChangeModal(true)}
            size="lg"
            className="bg-brand-orange text-white hover:bg-brand-orange-dark shadow-md hover:shadow-lg transition-all no-print px-6 flex-shrink-0"
            data-testid="acente-detail-change-cta-btn"
          >
            <Pencil size={16} className="mr-2" /> Değişiklik Yap
          </Button>
        </div>
      )}

      {/* Iter 39: Revize rozeti — kayıt oluşturulduktan sonra değişiklik yapıldıysa
          göze çarpan bir amber şeritte göster. Onay gerektirmez, sadece bilgi. */}
      {data.is_revised && (
        <div
          className="rounded-2xl border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
          data-testid="acente-detail-revised-banner"
        >
          <div className="w-9 h-9 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
            <Sparkles size={16}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold">Rezervasyon Revize Edildi</div>
            <div className="text-sm text-amber-900 mt-0.5">
              {data.revised_by_name ? <><b>{data.revised_by_name}</b> tarafından</> : "Sonradan"} güncellendi
              {data.revised_at && <> · <span className="text-amber-800/80">{new Date(data.revised_at).toLocaleString("tr-TR")}</span></>}
            </div>
          </div>
        </div>
      )}

      <ChangeRequestModal
        open={showChangeModal}
        onOpenChange={setShowChangeModal}
        reservation={data}
        onSubmitted={load}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Genel Bilgiler">
          <Row label="Otel">{data.hotel_name || <span className="text-slate-400 italic">Belirtilmemiş</span>}</Row>
          {data.customer_phone && <Row label="Müşteri Telefonu">{data.customer_phone}</Row>}
          <Row label="Giriş">{formatTr(data.checkin)}</Row>
          <Row label="Çıkış">{formatTr(data.checkout)}</Row>
          <Row label="Gece">{data.nights}</Row>
          {data.notes && <Row label="Not">{data.notes}</Row>}
        </Card>
        <Card title="Oda Dağılımı">
          <Row label="1 Kişilik">{data.rooms?.r1 ?? 0}</Row>
          <Row label="2 Kişilik">{data.rooms?.r2 ?? 0}</Row>
          <Row label="3 Kişilik">{data.rooms?.r3 ?? 0}</Row>
          <Row label="4 Kişilik">{data.rooms?.r4 ?? 0}</Row>
          <Row label="5 Kişilik">{data.rooms?.r5 ?? 0}</Row>
        </Card>
        <Card title="Yemek Bilgileri">
          <Row label="Yetişkin">{data.adults}</Row>
          <Row label="Çocuk">{data.children}</Row>
          <Row label="Görevli">{data.staff}</Row>
          <Row label="Başlangıç Öğünü">{MEAL_LABELS[data.meal_start] || data.meal_start || "—"}</Row>
          <Row label="Bitiş Öğünü">{MEAL_LABELS[data.meal_end] || data.meal_end || "—"}</Row>
        </Card>

        {/* Agreed prices — visible to ALL roles (agency's commercial contract).
            Özel acente rolünde "Alınacak Ücret" olarak etiketlenir. Para
            birimi tüm sistemde SAR (Suudi Riyali) — 2026-07 kararı. */}
        {((data.agreed_room_price || 0) > 0 || (data.agreed_meal_price || 0) > 0) && (
          <Card title={user?.role === "ozel_acente" ? "Alınacak Ücret" : "Anlaşılan Fiyat"} data-testid="acente-detail-agreed-prices">
            <Row label="Oda">
              {(data.agreed_room_price || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}{" "}
              <span className="text-slate-500 text-xs">SAR ﷼</span>
            </Row>
            <Row label="Yemek">
              {(data.agreed_meal_price || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}{" "}
              <span className="text-slate-500 text-xs">SAR ﷼</span>
            </Row>
            <Row label="Toplam">
              <b className="text-brand-orange-dark">
                {((data.agreed_room_price || 0) + (data.agreed_meal_price || 0)).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}{" "}
                <span className="text-slate-500 text-xs font-normal">SAR ﷼</span>
              </b>
            </Row>
          </Card>
        )}

        {canOpsApprove && !((data.total_price || 0) > 0) && (
          <Card title="Fiyat Onayı (Operasyon)">
            <div className="text-xs text-slate-600 mb-3">
              Acentenin girdiği fiyatı olduğu gibi onaylamak için tıklayın. Onay sonrası fiyat <b>kalıcı olarak kilitlenir</b>.
            </div>
            <div className="flex justify-end">
              <Button onClick={approveNow} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="acente-detail-ops-approve-inline">
                <CheckCircle2 size={14} className="mr-1"/> Fiyatı Onayla ve Kilitle
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Onaylanmış fiyat sabitlenmiş — tüm rollere görünür bilgi kartı */}
      {((data.total_price || 0) > 0) && (
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-5" data-testid="acente-detail-locked-price">
          <div className="flex items-center gap-2 text-emerald-800 mb-2">
            <CheckCircle2 size={16}/>
            <div className="text-xs uppercase tracking-widest font-semibold">Onaylanmış Fiyat (Kilitli)</div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3 text-sm">
            <div><div className="text-slate-500">Oda</div><b className="text-emerald-900">{Number(data.room_price || 0).toLocaleString("tr-TR")} <span className="text-xs font-normal">﷼</span></b></div>
            <div><div className="text-slate-500">Yemek</div><b className="text-emerald-900">{Number(data.meal_price || 0).toLocaleString("tr-TR")} <span className="text-xs font-normal">﷼</span></b></div>
            <div><div className="text-slate-500">Toplam</div><b className="text-emerald-900 text-lg">{Number(data.total_price || 0).toLocaleString("tr-TR")} <span className="text-xs font-normal">﷼</span></b></div>
          </div>
          {data.ops_note && (
            <div className="mt-3 text-xs text-emerald-800 bg-white/60 rounded-lg px-3 py-2">
              <b className="uppercase tracking-widest text-[10px] text-emerald-700">Operasyon Notu:</b>{" "}
              {data.ops_note}
            </div>
          )}
        </div>
      )}

      {canPrice && !((data.total_price || 0) > 0) && (
        <Card title="İç Operasyon — Fiyatlandırma (Manuel)">
          <div className="text-xs text-slate-600 mb-3">
            Bu alan sadece operasyona açıktır. Anlaşılan fiyat farklı ise burada manuel değişiklik yapıp kilitleyebilirsiniz.
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label>Oda Fiyatı</Label>
              <Input type="number" min="0" step="0.01" value={pricing.room_price} onChange={(e) => setPricing({ ...pricing, room_price: Number(e.target.value) })} data-testid="pricing-room-price" />
            </div>
            <div>
              <Label>Yemek Fiyatı</Label>
              <Input type="number" min="0" step="0.01" value={pricing.meal_price} onChange={(e) => setPricing({ ...pricing, meal_price: Number(e.target.value) })} data-testid="pricing-meal-price" />
            </div>
            <div>
              <Label>Toplam Fiyat</Label>
              <Input type="number" min="0" step="0.01" value={pricing.total_price} onChange={(e) => setPricing({ ...pricing, total_price: Number(e.target.value) })} data-testid="pricing-total-price" />
            </div>
          </div>
          <div className="mt-3">
            <Label>Operasyon Notu</Label>
            <Textarea value={pricing.ops_note} onChange={(e) => setPricing({ ...pricing, ops_note: e.target.value })} rows={2} data-testid="pricing-ops-note" />
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={savePricing} className="bg-brand-orange text-white" data-testid="pricing-save-btn">Fiyatları Kaydet</Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
      <div className="text-xs uppercase tracking-widest text-brand-navy/70 font-semibold mb-3">{title}</div>
      <div className="space-y-2 text-sm">{children}</div>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-4">
      <div className="text-slate-500">{label}</div>
      <div className="text-brand-navy font-medium text-right">{children}</div>
    </div>
  );
}
